
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PostgrestError } from '@supabase/supabase-js';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { addBotAccount } from '@/services/bot-account-service';
import { getExistingBotConnectionDetails } from '@/services/bot-connection-details';

interface FormData {
  platform: string;
  csp_account_id: string;
  trading_account_id: string;
  volume_multiplier: string;
  is_reverse_position: boolean;
}

interface TradingAccount {
  id: string;
  number_account_trading: string;
  api_connection_id: string;
  live: boolean;
  balance_account: number;
  isDisconnected?: boolean;
}

export function useStartChallengeForm(
  botId: string,
  onSuccess: () => void
) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoadingTradingAccounts, setIsLoadingTradingAccounts] = useState(false);
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccount[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      platform: 'Ctrader',
      csp_account_id: '',
      trading_account_id: '',
      volume_multiplier: '1',
      is_reverse_position: false
    }
  });

  const watchCspAccount = form.watch('csp_account_id');

  useEffect(() => {
    if (watchCspAccount) {
      form.setValue('trading_account_id', '');
      fetchTradingAccounts(watchCspAccount);
    }
  }, [watchCspAccount, form]);

  const fetchTradingAccounts = async (cspAccountId: string) => {
    setIsLoadingTradingAccounts(true);
    try {
      // Get all API connections for this CSP account with their status
      const { data: apiConnections, error: apiError } = await supabase
        .from('api_connections')
        .select('id, status')
        .eq('csp_account_id', cspAccountId);

      if (apiError) throw apiError;

      if (apiConnections && apiConnections.length > 0) {
        const apiConnectionIds = apiConnections.map(conn => conn.id);
        
        // Create a map of API connection status
        const apiConnectionStatusMap = new Map(
          apiConnections.map(conn => [conn.id, conn.status])
        );
        
        // Get all trading accounts
        const { data: accounts, error: accountsError } = await supabase
          .from('trading_accounts')
          .select('id, number_account_trading, api_connection_id, live, balance_account')
          .in('api_connection_id', apiConnectionIds);

        if (accountsError) throw accountsError;
        
        // Get all bot accounts that are connected to any bot regardless of status
        const { data: existingBotAccounts, error: botAccountsError } = await supabase
          .from('bot_accounts')
          .select('api_connection_id');
        
        if (botAccountsError) throw botAccountsError;
        
        // Filter out already connected accounts
        const usedApiConnectionIds = new Set(
          existingBotAccounts?.map(acc => acc.api_connection_id) || []
        );
        
        // Add isDisconnected flag to accounts from disconnected API connections
        const availableAccounts = accounts?.map(acc => ({
          ...acc,
          isDisconnected: apiConnectionStatusMap.get(acc.api_connection_id) === 'disconnected'
        })).filter(acc => 
          !usedApiConnectionIds.has(acc.api_connection_id)
        ) || [];
        
        setTradingAccounts(availableAccounts);
      } else {
        setTradingAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching trading accounts:', error);
      toast.error(t('failed_to_load_trading_accounts'));
    } finally {
      setIsLoadingTradingAccounts(false);
    }
  };

  const handleSubmit = async (data: FormData) => {
    if (!user?.id) {
      toast.error('You must be logged in to start a challenge');
      return;
    }

    const selectedAccount = tradingAccounts.find(acc => acc.id === data.trading_account_id);
    if (!selectedAccount) {
      toast.error('Invalid trading account selected');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Set the status based on whether the account is disconnected at the API level
      const status = selectedAccount.isDisconnected ? 'disconnected' : 'connected';
      
      await addBotAccount({
        bot_id: botId,
        user_id: user.id,
        api_connection_id: selectedAccount.api_connection_id,
        volume_multiplier: parseFloat(data.volume_multiplier),
        status: status,
        is_reverse_position: data.is_reverse_position
      });
      
      // Show appropriate message based on the account status and reverse position
      if (selectedAccount.isDisconnected) {
        toast.warning('Challenge started with disconnected account', {
          description: 'This account is disconnected in the CTrader Profile and needs to be reconnected before it will receive signals.'
        });
      } else {
        const message = data.is_reverse_position 
          ? 'Reverse position challenge started successfully'
          : 'Challenge started successfully';
        toast.success(message);
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error starting challenge:', error);
      console.log('Error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        details: (error as any)?.details,
        hint: (error as any)?.hint
      });
      
      // Enhanced error handling for unique constraint violations
      const isUniqueConstraintError = 
        ((error as PostgrestError).code === 'P0001' && 
         (error as PostgrestError).message.includes('already exists')) ||
        ((error as PostgrestError).code === '23505') ||
        ((error as any)?.message?.includes('duplicate key value violates unique constraint'));
      
      if (isUniqueConstraintError) {
        // Try to get detailed information about the existing connection
        try {
          const existingDetails = await getExistingBotConnectionDetails(selectedAccount.api_connection_id);
          
          if (existingDetails) {
            const reverseText = existingDetails.isReversePosition ? ' đảo ngược vị thế' : '';
            toast.error('Tài khoản đã được kết nối', {
              description: `Tài khoản ${existingDetails.accountNumber} đã được kết nối với bot ${existingDetails.botName}${reverseText}, vui lòng kiểm tra lại.`
            });
          } else {
            // Fallback to generic message if we can't get details
            toast.error('Trading Account Already Connected', {
              description: 'This trading account is already connected to another bot.'
            });
          }
        } catch (detailError) {
          console.error('Error getting connection details:', detailError);
          toast.error('Trading Account Already Connected', {
            description: 'This trading account is already connected to another bot.'
          });
        }
        
        return; // Don't call onSuccess, keep the form open
      }
      toast.error('Failed to start challenge');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isLoadingTradingAccounts,
    tradingAccounts,
    isSubmitting,
    handleSubmit
  };
}
